import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, MapPin, Calendar, Sparkles, Heart, Users, BookOpen, Award, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { parseYMD } from "@/lib/dates";
import Mandala from "@/components/Mandala";
import ShareButton from "@/components/ShareButton";
import SEO from "@/components/SEO";
import InsuranceCallout from "@/components/InsuranceCallout";

// ─── Fallback images (used when Holly hasn't curated a slot yet) ───
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const fb = (id) => `${BACKEND}/api/files/${id}`;
const FALLBACK = {
  hero:       fb("a5304926-c386-4344-b652-b4aacd7a7b89"),
  "wings-main":      fb("3a90d085-eac8-47f5-b403-37fc7ddc17df"),
  "wings-accent":    [fb("3855ea74-337b-49ae-9d44-47a3d6bb4a43"), fb("c40cb2ef-b933-40cb-8519-fa6478cd7cc7")],
  "practice-mosaic": [fb("6a0fc213-002f-4583-aec5-c3facc71807a"), fb("d67c0a5a-d592-4741-a7a6-f1f0b6303542"), fb("65ef6e0f-a90e-47d4-9860-fdb0b803164d"), fb("14c9d4a8-bd1f-4396-b685-da54ab8b70f4"), fb("79f0b8d1-6c49-4eac-8467-b49430cc1426"), fb("df08fd90-97a6-441f-a692-1de867ebc63d"), fb("a71f4a1a-5293-413e-83aa-c1c90872d18c")],
  "day-1":    fb("04fa4ba1-8d12-4e6f-b75e-3014a19c0eaf"),
  "day-2":    fb("6f180450-5dba-43a7-8cef-f4dd03823052"),
  "day-3":    fb("153e6315-dd45-46f1-981f-3c89c82544ed"),
  sisterhood: [fb("e9a228c4-e620-4a0f-9d34-5cf8435298ec"), fb("c93e979e-2fb8-4712-ba75-833acff43d57"), fb("a38b2f52-432f-4c3f-83f1-12bcd16dd9cb")],
  "holly-portrait":  fb("f2989194-b3e2-49b0-a498-6f650812b85d"),
  "holly-teaching":  fb("2954dd4f-d431-4797-a7c8-838938f7b6cb"),
  "closing-bg":      fb("16a94234-05c9-4634-aab9-42dcb157da59"),
  "training-card-1": fb("04fa4ba1-8d12-4e6f-b75e-3014a19c0eaf"),
  "training-card-2": fb("153e6315-dd45-46f1-981f-3c89c82544ed"),
};

// Resolve a slot — admin-curated values win, fallback otherwise.
// `loaded` controls whether we're allowed to fall back to the hard-coded defaults.
// While the page-images API is still in flight we DON'T fall back — that way Holly's
// curated photos never appear to be "ignored" as the page first paints.
function resolveSlot(slots, key, loaded) {
  const curated = slots[key];
  if (Array.isArray(curated) && curated.length) {
    return curated.map((u) => (u.startsWith("http") ? u : `${BACKEND}${u}`));
  }
  if (!loaded) return [];
  return Array.isArray(FALLBACK[key]) ? FALLBACK[key] : [FALLBACK[key]].filter(Boolean);
}
const pick1 = (slots, key, loaded) => resolveSlot(slots, key, loaded)[0];

// ─── Sacred mandalas (uploaded by Holly) ───
const M = {
  lens:   "/mandalas/lens.png",
  health: "/mandalas/health.png",
  body:   "/mandalas/body.png",
};

const PILLARS = [
  { icon: BookOpen, title: "Curriculum", lines: ["50 Hour Aerial Yoga Teacher Training", "Trainees practice teach during the training", "Honors traditional yogic principles", "5-month online modules window (extensions available)", "Career support included"] },
  { icon: Heart,    title: "Tuition",    lines: ["Interest-free payment plans", "Weekend-intensive format", "Early-bird savings available", "$350 deposit secures your spot"] },
  { icon: Award,    title: "Trainer",    lines: ["Master-level instructor — Holly Johnson", "Over 10 years guiding teachers", "Personal mentorship + coaching", "Accessible after the training"] },
  { icon: Users,    title: "Community",  lines: ["Sisterhood that begins during training", "Open-minded, supportive women", "Private Sisters of the Silks group", "Connected & actively engaged"] },
];

const CURRICULUM = [
  { t: "Principles of Aviana Aerial Yoga", d: "Aligned with the planets, moon, and sun — these principles help you shine as a powerful instructor of integrity." },
  { t: "Principles of Yoga", d: "Maintain the integrity of yoga in your aerial classes without crossing into circus performance — yoga as moving meditation." },
  { t: "Body-Intelligent Sequencing", d: "Structure a class that flows gracefully, opening through muscular chains so students find ease in challenging asanas." },
  { t: "Transitions for Elegance & Grace", d: "Yoga isn't only the pose — it's the connection between poses. Learn to move energy through every transition." },
  { t: "Modifications & Spotting", d: "Hands-on training to keep your students safe with confidence as they branch into new asanas with the hammock." },
  { t: "60+ Aerial Yoga Asanas", d: "Traditional yoga poses adapted for the hammock plus performance aerial-sling shapes as mindful yogic expressions." },
  { t: "Two Full Class Sequences", d: "Two distinct teaching styles to expand your toolbox so you can create sequences for any class intention." },
  { t: "Aromatherapy 101", d: "When essential oils meet the sensory input of the hammock, your class becomes a whole new level of experience." },
  { t: "Chakra Therapy", d: "Energy centers as the methodology behind sequencing — enhance your classes with deeper meaning and intention." },
  { t: "Business & Industry", d: "Aerial yoga is booming. Everything to consider when getting started and how to set yourself up for success." },
  { t: "Goddess Archetypes", d: "Each day we learn an archetype (Lakshmi, Kuan Yin, Sophia) as sources of inspiration for class creation and personal journey." },
  { t: "Aerial Yoga Niche Specialties", d: "Time to explore the lightbulbs of inspiration that ignite as you step into sharing this practice with others." },
];

const FAQS = [
  { q: "What is the Aviana style of aerial yoga?", a: "Aviana offers an aerial yoga practice where you maintain a connection with the hammock throughout, allowing you to surrender into being held while flowing through a moving meditation. Great attention is given to body alignment and your relationship to the anchor points. From this rooted foundation, the practice takes on a full sensory engagement — sights, sounds, aromas — guiding the practitioner into a meditative journey of growth with lengthened, toned elegance." },
  { q: "I've never taken an aerial yoga class — can I attend?", a: "Absolutely. You won't be alone. Many instructors enroll to experience aerial for the first time. Sometimes it's even better to enter with a beginner's mind." },
  { q: "Where can I do my practice-teaching hours?", a: "We'll discuss this at the training to make sure you have a place to hang at least two hammocks. If you live near the hosting studio, there's often an arrangement to complete hours there." },
  { q: "What are the certification requirements?", a: "Complete the in-person intensive, then complete the remaining four modules (Exam · Research Paper · 20 hours practice teaching · Class Audit video) within 5 months. All modules receive feedback and must meet Aviana's safety standards." },
  { q: "How soon can I receive my certification?", a: "Some students complete everything within 2 months of the intensive. You have up to 5 months, with extensions available if needed." },
  { q: "Is there a payment plan?", a: "Yes — interest-free payment plans break your tuition into 3 or 4 monthly payments." },
  { q: "How is this training different from others?", a: "Intentional focus on optimal body alignment, moving meditative sequences, full sensory engagement, and the power of sisterhood. The intensive feels like a retreat infused with a teacher training." },
  { q: "What if I struggle with test-taking?", a: "The examination is straightforward — everything on it is covered in the intensive or your training text. Short, not stressful, and you can retake once if needed." },
  { q: "Will I receive support after the training?", a: "Yes — Holly is here through every step, and the Sisters of the Silks community group keeps you connected to other graduates around the globe." },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-aviana-border" data-testid={`faq-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left gap-4 group">
        <span className="font-display text-[18px] text-aviana-ink group-hover:text-aviana-plum transition-colors">{q}</span>
        <ChevronDown size={18} className={`text-aviana-gold transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 text-aviana-inkSoft leading-[1.75] text-[15px]">{a}</div>}
    </div>
  );
}

// ─── Vimeo testimonial videos (id, h-hash for private playback, label) ───
const VIDEO_TESTIMONIALS = [
  { id: "1022340200", h: "736a60c64e", label: "Feedback from an Aviana Teacher in Training" },
  { id: "1022314382", h: "b372fdf399", label: "Testimonial from the 2024 Aviana Teacher Training" },
  { id: "1022321935", h: "b85606d0b7", label: "Learn about the Aviana Teacher Training from a Student" },
  { id: "1022341024", h: "7b5ebe06fa", label: "A Personal Experience with the Aviana Aerial Yoga Teacher Training" },
  { id: "1022315636", h: "986ef420ac", label: "Feedback about the Aviana Aerial Yoga Teacher Training" },
];

function VideoTestimonialCarousel() {
  const scrollerRef = React.useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-vt-card]");
    const step = card ? card.getBoundingClientRect().width + 20 : 420;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };
  return (
    <div className="relative" data-testid="video-testimonials">
      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-6 px-4 md:px-8 -mx-4 md:-mx-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {VIDEO_TESTIMONIALS.map((v, i) => (
          <article
            key={v.id}
            data-vt-card
            data-testid={`video-testimonial-${i}`}
            className="snap-start shrink-0 w-[88%] sm:w-[480px] md:w-[560px] bg-aviana-plumDeep/60 border border-aviana-gold/25 rounded-2xl overflow-hidden"
          >
            <div className="relative aspect-video bg-aviana-ink">
              <iframe
                src={`https://player.vimeo.com/video/${v.id}?h=${v.h}&title=0&byline=0&portrait=0&badge=0&autopause=0`}
                title={v.label}
                loading="lazy"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 w-full h-full"
              />
            </div>
            <div className="px-6 py-4">
              <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-goldSoft/80 mb-1.5">Sister of the silks</p>
              <p className="text-aviana-cream/90 font-serif italic text-[14px] leading-[1.55]">{v.label}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="flex justify-center gap-3 mt-4">
        <button
          onClick={() => scrollBy(-1)}
          aria-label="Scroll testimonials left"
          data-testid="video-testimonials-prev"
          className="w-11 h-11 rounded-full border border-aviana-gold/40 text-aviana-goldSoft hover:bg-aviana-gold/15 transition-colors flex items-center justify-center"
        >
          <ArrowRight size={16} className="rotate-180" />
        </button>
        <button
          onClick={() => scrollBy(1)}
          aria-label="Scroll testimonials right"
          data-testid="video-testimonials-next"
          className="w-11 h-11 rounded-full border border-aviana-gold/40 text-aviana-goldSoft hover:bg-aviana-gold/15 transition-colors flex items-center justify-center"
        >
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function TrainingCard({ offering, image }) {
  if (!offering) return null;
  const start = parseYMD(offering.start_date);
  const end = parseYMD(offering.end_date);
  const fmt = (d) => d?.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  const dateRange = end ? `${fmt(start)} – ${end.toLocaleDateString("en-US",{day:"numeric"})}, ${start.getFullYear()}` : fmt(start);
  const earlyBird = offering.early_bird_until && new Date(offering.early_bird_until) > new Date();
  const earlyBirdDeadline = offering.early_bird_until && new Date(offering.early_bird_until).toLocaleDateString("en-US",{month:"long",day:"numeric"});
  const exclusive = !!offering.exclusive_to_studio;
  // Prefer the offering's own image_url when one is set; otherwise use Holly's
  // curated training-card slot image. This lets new trainings (like ASNA) ship
  // with their venue-specific photo without waiting for slot curation.
  const cardImage = offering.image_url || image;
  return (
    <article className="bg-aviana-cream rounded-2xl border border-aviana-gold/30 overflow-hidden flex flex-col shadow-xl shadow-aviana-plum/5 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500" data-testid={`training-card-${offering.slug}`}>
      <div className="aspect-[16/10] overflow-hidden relative">
        <img src={cardImage} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep/70 via-aviana-plumDeep/20 to-transparent" />
        {exclusive && (
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-aviana-cream bg-aviana-plum/95 backdrop-blur-sm rounded-full font-medium" data-testid={`training-exclusive-${offering.slug}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-aviana-goldSoft" />
              Exclusive · {offering.exclusive_studio_name?.split(" ")[0] || "Studio"} only
            </span>
          </div>
        )}
        <div className="absolute bottom-5 left-6 right-6 text-aviana-cream">
          <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-goldSoft flex items-center gap-2"><MapPin size={12} /> {offering.location}</p>
        </div>
      </div>
      <div className="p-8 lg:p-10 flex-1">
        <h3 className="font-display text-3xl text-aviana-ink leading-tight">{offering.title.replace(/^Aerial Yoga Teacher Training\s*(at\s+|·\s+)?/i, "").trim() || offering.title}</h3>
        <p className="mt-2 text-aviana-gold text-[14px] tracking-[0.15em] uppercase">{dateRange}</p>
        {offering.venue && <p className="mt-3 text-aviana-muted text-[14px] italic">@ {offering.venue}</p>}
        <p className="mt-5 text-aviana-inkSoft leading-[1.75] text-[15px]">{offering.short_description}</p>
        {exclusive && (
          <div className="mt-6 rounded-xl bg-aviana-plum/5 border border-aviana-plum/25 px-4 py-3" data-testid={`training-exclusive-note-${offering.slug}`}>
            <p className="text-[12px] text-aviana-inkSoft leading-[1.6]">
              <strong className="text-aviana-plum">Exclusive to {offering.exclusive_studio_name || "the hosting studio"}.</strong>{" "}
              Registration links are shared by the studio manager.
            </p>
            <Link
              to="/host-a-training"
              className="mt-2 inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-aviana-goldDeep hover:text-aviana-plum underline underline-offset-4"
              data-testid={`training-host-your-own-${offering.slug}`}
            >
              Host this at your studio → 
            </Link>
          </div>
        )}
        <div className="mt-7 bg-white rounded-xl border border-aviana-border p-5">
          {earlyBird && offering.price_regular_usd ? (
            <>
              <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-plum">Early-bird tuition</p>
              <p className="font-display text-4xl text-aviana-ink mt-1">${offering.price_usd.toLocaleString()}</p>
              <p className="text-[12px] text-aviana-muted mt-1">
                <span className="line-through">${offering.price_regular_usd.toLocaleString()}</span> regular · Early-bird ends <strong>{earlyBirdDeadline}</strong>
              </p>
            </>
          ) : (
            <>
              <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-plum">Tuition</p>
              <p className="font-display text-4xl text-aviana-ink mt-1">${offering.price_usd?.toLocaleString()}</p>
            </>
          )}
          <p className="text-[12px] text-aviana-muted mt-2">Interest-free payment plans · ${offering.deposit_usd} deposit secures your spot</p>
        </div>
        <p className="mt-4 text-[12px] text-aviana-muted flex items-center gap-1"><Calendar size={12} /> Friday – Sunday · 10 am – 7 pm each day</p>
        {typeof offering.spots_left === "number" && offering.spots_left <= 5 && (
          <p className="mt-3 text-[12px] tracking-[0.12em] uppercase text-red-700/90 font-semibold">Only {offering.spots_left} {offering.spots_left === 1 ? "spot" : "spots"} left</p>
        )}
      </div>
      <Link to={`/offering/${offering.slug}`} className="btn-gold !rounded-none !justify-center !py-4" data-testid={`register-${offering.slug}`}>
        {exclusive ? "View training details" : `Secure your spot · $${offering.deposit_usd} deposit`} <ArrowRight size={14} />
      </Link>
    </article>
  );
}

export default function AerialYTT() {
  const [trainings, setTrainings] = useState([]);
  const [slots, setSlots] = useState({});
  const [slotsLoaded, setSlotsLoaded] = useState(false);

  useEffect(() => {
    // Fire both requests in parallel — the page-images fetch must not be blocked
    // by the offerings call, otherwise the hero/sisterhood/etc. linger on
    // fallback photos for far too long.
    api.get("/offerings?type=training").then(({ data }) => {
      const todayMs = new Date().setHours(0, 0, 0, 0);
      const upcoming = data
        .filter((o) => {
          const d = parseYMD(o.start_date);
          return d && d.getTime() >= todayMs;
        })
        .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
      setTrainings(upcoming);
    }).catch(() => {});
    api.get("/page-images/aerial-ytt").then(({ data }) => {
      setSlots(data.slots || {});
    }).catch(() => {}).finally(() => setSlotsLoaded(true));
  }, []);

  // Resolve image slots once per render — curated wins, fallback only after slots load
  const hero = pick1(slots, "hero", slotsLoaded);
  const wingsMain = pick1(slots, "wings-main", slotsLoaded);
  const mosaic = resolveSlot(slots, "practice-mosaic", slotsLoaded);
  const day1 = pick1(slots, "day-1", slotsLoaded);
  const day2 = pick1(slots, "day-2", slotsLoaded);
  const day3 = pick1(slots, "day-3", slotsLoaded);
  const sisters = resolveSlot(slots, "sisterhood", slotsLoaded);
  const hollyPortrait = pick1(slots, "holly-portrait", slotsLoaded);
  const closingBg = pick1(slots, "closing-bg", slotsLoaded);
  const trainingCardImgs = [pick1(slots, "training-card-1", slotsLoaded), pick1(slots, "training-card-2", slotsLoaded)];

  return (
    <div className="bg-aviana-paper" data-testid="aerial-ytt-page">
      <SEO
        title="Aerial Yoga Teacher Training · 50hr YACEP Certification with Holly Johnson"
        description="Yoga Alliance YACEP-approved 50-hour aerial yoga teacher training with E-RYT 500 Holly Johnson. In-person + online cohorts in 2026. Curriculum covers asana, sequencing, rigging, safety, and energy. 5-month online modules window included."
        path="/trainings/aerial-yoga"
        image="/social/og-aerial-ytt.jpg"
        keywords="aerial yoga teacher training, aerial yoga certification, YACEP aerial yoga, 50hr aerial yoga training, aerial yoga insurance, aerial yoga book, restorative aerial yoga, Holly Johnson, learn to teach aerial yoga, Yoga Alliance aerial training"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Aerial Yoga Teacher Training — 50hr YACEP",
          "description": "Yoga Alliance YACEP-approved 50-hour aerial yoga teacher training. Curriculum covers asana, sequencing, rigging, safety, and energy.",
          "provider": {"@id": "https://avianayoga.com/#organization"},
          "instructor": {"@id": "https://avianayoga.com/#holly"},
          "courseCode": "AYTT-50",
          "educationalCredentialAwarded": "50-hour Yoga Alliance YACEP Certificate",
          "hasCourseInstance": trainings.slice(0, 3).map((t) => ({
            "@type": "CourseInstance",
            "name": t.title,
            "startDate": t.start_date,
            "endDate": t.end_date,
            "location": {"@type": "Place", "name": t.location},
            "courseMode": "Onsite"
          }))
        }}
      />

      {/* ═══════════ HERO — matches About page aesthetic ═══════════ */}
      <section className="relative min-h-[85svh] flex items-center overflow-hidden bg-aviana-plumDeep text-aviana-cream">
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover object-[40%_center]" />
        <div className="absolute inset-0 bg-gradient-to-r from-aviana-plumDeep via-aviana-plumDeep/80 to-aviana-plumDeep/10" />
        <div className="absolute inset-0 grain pointer-events-none opacity-50" aria-hidden />
        <Mandala size={780} variant="full" className="hidden md:block absolute -right-32 -bottom-40 text-aviana-gold opacity-[0.18] animate-spin-slow" />
        <div className="container-aviana relative z-10 py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-7">
              <Mandala size={28} variant="lotus" className="text-aviana-goldSoft" />
              <p className="overline text-aviana-goldSoft">50 Hour Aerial Yoga Teacher Training · Since 2014</p>
              <Mandala size={28} variant="lotus" className="text-aviana-goldSoft" />
            </div>
            <h1 className="font-display text-[64px] sm:text-[88px] lg:text-[110px] leading-[0.92]" data-testid="hero-headline">
              Aerial Yoga
              <br />
              <em className="italic font-serif gold-text">Teacher Training.</em>
            </h1>
            <p className="mt-10 font-serif italic text-[20px] md:text-[24px] leading-[1.45] text-aviana-cream/90 max-w-xl">
              <strong className="not-italic font-medium text-aviana-cream">Three days</strong> of moving meditation, mentorship, and sisterhood — for women called to teach with <em>confidence, integrity, and embodied awareness.</em>
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <a href="#upcoming" className="btn-gold" data-testid="hero-cta-trainings">
                <Sparkles size={14} /> Discover your wings
              </a>
              <Link to="/courses/online-restorative-aerial-yoga-teacher-training" className="text-aviana-goldSoft hover:text-aviana-cream text-[13px] tracking-[0.18em] uppercase border-b border-aviana-goldSoft/40 pb-1" data-testid="hero-cta-online">
                or learn online →
              </Link>
              <ShareButton
                title="50 Hour Aerial Yoga Teacher Training with Holly Johnson"
                slug="aerial-yoga-teacher-training"
                kind="training"
                label="Send to a sister"
              />
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-5 text-[11px] tracking-[0.28em] uppercase text-aviana-goldSoft">
              <span>50 Hour Aerial Yoga Teacher Training</span>
              <span className="w-6 h-px bg-aviana-goldSoft/40" />
              <span>12+ Years guiding</span>
              <span className="w-6 h-px bg-aviana-goldSoft/40" />
              <span>40+ Studios launched</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ DISCOVER YOUR WINGS — single oval image ═══════════ */}
      <section className="py-32">
        <div className="container-aviana grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] rounded-[200px] overflow-hidden bg-aviana-plum/10">
              <img src={wingsMain} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <p className="overline-plum mb-4">Discover your wings</p>
            <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.02]">
              More than a certification —<br /><span className="italic text-aviana-plum">a return to your own practice.</span>
            </h2>
            <div className="mt-8 space-y-5 text-aviana-inkSoft text-[17px] leading-[1.8]">
              <p>This training is not about memorizing poses or performing shapes. It is about learning how to <em className="text-aviana-plum not-italic font-semibold">guide responsibly, cue intelligently, and hold space</em> for real human bodies — while deepening your own relationship with the practice.</p>
              <p>Since 2014, instructors from around the world have completed the Aviana training and gone on to teach thoughtful, body-intelligent classes rooted in yoga philosophy, mindful movement, and moving meditation. As a graduate, you join the <strong>Sisters of the Silks</strong> — a growing lineage of teachers devoted to offering aerial yoga as a practice of empowerment, care, and conscious connection.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ THE PRACTICE (image mosaic) ═══════════ */}
      <section className="py-24 bg-aviana-cream/40">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline-plum mb-3">The practice</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-tight">Surrender into being held.</h2>
            <p className="mt-5 text-aviana-muted text-[16px] italic">Where the hammock becomes the breath of the practice — and you become both student and teacher.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto" data-testid="practice-mosaic">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden md:row-span-1"><img src={mosaic[0]} alt="" className="w-full h-full object-cover" /></div>
            <div className="aspect-[4/5] rounded-2xl overflow-hidden md:translate-y-8"><img src={mosaic[1]} alt="" className="w-full h-full object-cover" /></div>
            <div className="aspect-[4/5] rounded-2xl overflow-hidden"><img src={mosaic[2]} alt="" className="w-full h-full object-cover" /></div>
          </div>
        </div>
      </section>

      {/* ═══════════ UPCOMING TRAININGS ═══════════ */}
      <section id="upcoming" className="py-32 relative overflow-hidden" data-testid="upcoming-trainings">
        <img src={M.health} alt="" className="absolute -top-32 -left-20 w-[500px] opacity-[0.07] pointer-events-none hidden lg:block" style={{filter:"sepia(1) saturate(2) hue-rotate(15deg)"}} />
        <img src={M.lens} alt="" className="absolute -bottom-32 -right-20 w-[460px] opacity-[0.07] pointer-events-none hidden lg:block" style={{filter:"sepia(1) saturate(2) hue-rotate(280deg)"}} />
        <div className="container-aviana relative">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline-plum mb-3">Reserve your seat</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-tight">Upcoming trainings.</h2>
            <p className="mt-5 text-aviana-muted">Two immersive trainings remain in 2026. Each one limited to a small group to preserve the depth and sisterhood at the heart of this program.</p>
          </div>
          {trainings.length === 0 ? (
            <p className="text-center text-aviana-muted">New 2026 trainings will be announced soon — join the newsletter to be the first to know.</p>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {trainings.map((o, i) => <TrainingCard key={o.slug} offering={o} image={trainingCardImgs[i]} />)}
            </div>
          )}
        </div>
      </section>

      {/* ═══════════ WHY CHOOSE AVIANA ═══════════ */}
      <section className="py-32 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <img src={M.lens} alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] opacity-[0.04] pointer-events-none" style={{filter:"invert(1) sepia(1) saturate(2) hue-rotate(15deg)"}} />
        <div className="container-aviana relative">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline text-aviana-goldSoft mb-3">Why choose Aviana</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-cream leading-tight">A training that feels like<br /><span className="italic text-aviana-goldSoft">a retreat.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map(({ icon: Icon, title, lines }) => (
              <div key={title} className="bg-aviana-plumDeep/60 backdrop-blur border border-aviana-gold/30 rounded-2xl p-7 hover:border-aviana-goldSoft transition-colors">
                <Icon size={24} className="text-aviana-goldSoft mb-5" />
                <h3 className="font-display text-2xl text-aviana-cream mb-4">{title}</h3>
                <ul className="space-y-2.5 text-aviana-cream/85 text-[13px] leading-[1.55]">
                  {lines.map((l) => <li key={l} className="flex gap-2"><Check size={14} className="text-aviana-goldSoft shrink-0 mt-0.5" /> {l}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 3-DAY JOURNEY ═══════════ */}
      <section className="py-32">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline-plum mb-3">The 3-day intensive</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-tight">Three days.<br /><span className="italic text-aviana-plum">One lineage.</span></h2>
          </div>
          <div className="space-y-7 max-w-5xl mx-auto" data-testid="days-list">
            {[
              { d: "Day One",   t: "The Practice", img: day1, b: "A full 75-minute class drops you into flow. Then tutorials and time to practice individual asanas — grounding, warming, vinyasa-style aerial flow, and your first hammock-supported inversions." },
              { d: "Day Two",   t: "The Details",  img: day2, b: "Continue learning aerial yoga poses. Discover your voice as an instructor. Study contraindications and equipment. Learn to tie a hammock. Connect poses to the major energy centers with aromatherapy support." },
              { d: "Day Three", t: "The Union",    img: day3, b: "Develop your presence and voice. Learn the key points to leading an aerial class and building a thriving business. Design sequences in your personal style — and receive a hammock photoshoot to empower your marketing." },
            ].map((s, i) => (
              <article key={s.d} className={`grid md:grid-cols-12 gap-6 items-center ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                <div className={`md:col-span-5 aspect-[4/3] rounded-2xl overflow-hidden ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <img src={s.img} alt={s.t} className="w-full h-full object-cover" />
                </div>
                <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <p className="overline-plum mb-3 text-aviana-gold">{s.d}</p>
                  <h3 className="font-display text-4xl md:text-5xl text-aviana-ink">{s.t}</h3>
                  <p className="mt-4 text-aviana-inkSoft text-[17px] leading-[1.8]">{s.b}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-16 text-center text-aviana-muted max-w-2xl mx-auto text-[15px]">After the intensive, you'll have <strong className="text-aviana-ink">5 months</strong> to complete four supportive modules — <em>Examination · Research Paper · 20 Hours Practice Teaching · Class Audit Video</em> — all graded with personal feedback by Holly.</p>
        </div>
      </section>

      {/* ═══════════ CURRICULUM ═══════════ */}
      <section className="py-32 bg-aviana-cream/40 relative overflow-hidden">
        <img src={M.health} alt="" className="absolute top-12 -right-32 w-[440px] opacity-[0.08] pointer-events-none hidden lg:block" style={{filter:"sepia(1) saturate(2) hue-rotate(280deg)"}} />
        <div className="container-aviana relative">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline-plum mb-3">The curriculum</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-tight">What you'll <span className="italic text-aviana-plum">learn.</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 max-w-6xl mx-auto" data-testid="curriculum-grid">
            {CURRICULUM.map(({ t, d }, i) => (
              <div key={t} className="border-l border-aviana-gold/40 pl-5">
                <p className="font-display text-[11px] tracking-[0.22em] uppercase text-aviana-gold">{String(i+1).padStart(2,"0")}</p>
                <h3 className="font-display text-xl text-aviana-ink mt-1">{t}</h3>
                <p className="mt-2 text-aviana-inkSoft text-[14px] leading-[1.7]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SISTERHOOD / SOCIAL PROOF ═══════════ */}
      <section className="py-32 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <img src={M.body} alt="" className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] opacity-[0.06] pointer-events-none" style={{filter:"invert(1) sepia(1) saturate(3) hue-rotate(15deg)"}} />
        <div className="container-aviana relative">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline text-aviana-goldSoft mb-3">The sisterhood</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-cream leading-tight">Once you find your wings,<br /><span className="italic text-aviana-goldSoft">you fly together.</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { img: sisters[0], q: "Holly has a gift for holding space. The training felt like both a homecoming and a launching pad. I left knowing exactly who I wanted to be as a teacher.", name: "Sister of the Silks · 2025 cohort" },
              { img: sisters[1], q: "I came to learn poses. I left with a lineage. The connection to the energy centers, the goddess archetypes, the body-intelligent sequencing — this is a master class wrapped in a retreat.", name: "Sister of the Silks · 2024 cohort" },
              { img: sisters[2], q: "Three days that re-aligned everything I thought I knew about teaching. Holly's care for safety, her depth, her presence — I'd recommend this to anyone called to guide.", name: "Sister of the Silks · 2024 cohort" },
            ].map((t, i) => (
              <article key={i} className="bg-aviana-plumDeep/60 backdrop-blur border border-aviana-gold/25 rounded-2xl overflow-hidden">
                <div className="aspect-[5/3] overflow-hidden">
                  <img src={t.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-7">
                  <Quote size={20} className="text-aviana-goldSoft mb-3" />
                  <p className="text-aviana-cream/90 text-[14px] leading-[1.75] font-serif italic">{t.q}</p>
                  <p className="mt-5 text-[10px] tracking-[0.22em] uppercase text-aviana-goldSoft/80">{t.name}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ VIDEO TESTIMONIALS ═══════════ */}
      <section className="py-32 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden border-t border-aviana-gold/10">
        <Mandala size={680} variant="full" className="absolute -left-40 top-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.06] pointer-events-none" />
        <div className="container-aviana relative">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="overline text-aviana-goldSoft mb-3">Hear from the sisterhood</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-cream leading-tight">In their own <span className="italic text-aviana-goldSoft">words.</span></h2>
            <p className="mt-5 text-aviana-cream/80 text-[15px] leading-[1.7]">Five graduates share what the Aviana Aerial Yoga Teacher Training meant to them — scroll through to hear their stories.</p>
          </div>
          <VideoTestimonialCarousel />
        </div>
      </section>

      {/* ═══════════ MEET HOLLY ═══════════ */}
      <section className="py-32">
        <div className="container-aviana grid lg:grid-cols-12 gap-12 items-center max-w-5xl">
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-aviana-plum/10">
              <img src={hollyPortrait} alt="Holly Johnson" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <p className="overline-plum mb-3">Meet your trainer</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-tight">Holly Johnson.</h2>
            <p className="mt-2 text-aviana-gold italic text-[16px]">Founder of Aviana · Author · Master-level instructor</p>
            <div className="mt-7 space-y-5 text-aviana-inkSoft leading-[1.8] text-[16px]">
              <p>I began teaching aerial yoga in 2012 in Fort Collins, Colorado — the first to introduce the practice to the area. I know the courage it takes to step into something new and find your way with it to a life you love.</p>
              <p>Since then, I've traveled the country and the globe supporting <strong className="text-aviana-plum">over 40 studios, retreat centers, and arboretums</strong> with launching their own aerial yoga programs. I am the author of <em>Soaring with the Soul: An Aerial Yoga Guide</em> — currently writing the second edition.</p>
              <p>I feel so blessed Aviana has come into my life as the portal through which I support women in becoming teachers who serve the wellness and mindfulness of their local communities.</p>
            </div>
            <p className="mt-7 italic text-aviana-plum font-serif text-[18px]">Many Blessings of Love, Light and Flight!</p>
          </div>
        </div>
      </section>

      <InsuranceCallout />

      {/* ═══════════ FAQS ═══════════ */}
      <section className="py-32 bg-aviana-cream/40">
        <div className="container-aviana max-w-3xl">
          <div className="text-center mb-14">
            <p className="overline-plum mb-3">Questions, answered</p>
            <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-tight">Frequently <span className="italic text-aviana-plum">asked.</span></h2>
          </div>
          <div>
            {FAQS.map((fq, i) => <FaqItem key={i} {...fq} idx={i} />)}
          </div>
        </div>
      </section>

      {/* ═══════════ HOST A TRAINING (new CTA) ═══════════ */}
      <section className="py-24 bg-aviana-cream/40 border-y border-aviana-border">
        <div className="container-aviana max-w-4xl text-center">
          <p className="overline-plum mb-3">For studios &amp; retreat centers</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-tight">Interested in scheduling a training at your studio?</h2>
          <p className="mt-5 text-aviana-inkSoft leading-[1.75] max-w-2xl mx-auto text-[16px]">Holly partners with studios and retreat centers around the globe to launch new aerial yoga programs. From rigging consultation to a fully hosted 50-Hour training, she'll help you bring this practice to your community.</p>
          <Link to="/host-a-training" className="btn-gold mt-9 inline-flex" data-testid="host-training-cta">
            Bring Aviana to your studio <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ═══════════ FINAL CTA — clean, no mandala overlay ═══════════ */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={closingBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-aviana-ink/80 via-aviana-plumDeep/85 to-aviana-ink/90" />
        </div>
        <div className="container-aviana relative text-center max-w-2xl text-aviana-cream">
          <p className="overline text-aviana-goldSoft mb-5 tracking-[0.32em]">The call is gentle. Your answer is everything.</p>
          <h2 className="font-display text-5xl md:text-6xl leading-tight">Ready to discover<br /><span className="italic text-aviana-goldSoft">your wings?</span></h2>
          <p className="mt-7 text-aviana-cream/80 leading-[1.8] text-[17px]">Two trainings remain in 2026. Spaces are intentionally limited to preserve depth, safety, and sisterhood. A $350 deposit secures your seat — and your seat in the lineage.</p>
          <div className="mt-12 flex flex-wrap justify-center gap-5">
            <a href="#upcoming" className="btn-gold !text-[13px] !px-8 !py-4" data-testid="footer-cta-trainings">See upcoming trainings <ArrowRight size={14} /></a>
            <Link to="/courses/online-restorative-aerial-yoga-teacher-training" className="text-aviana-goldSoft hover:text-aviana-cream text-[13px] tracking-[0.18em] uppercase border-b border-aviana-goldSoft/40 pb-1" data-testid="footer-cta-online">
              Or learn online →
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ YOUR PATH FORWARD — teaser for the trainer Legacy Path ═══════════ */}
      <section className="py-24 md:py-28 bg-aviana-paper" data-testid="your-path-forward">
        <div className="container-aviana max-w-3xl text-center">
          <span className="ornament-flower mx-auto block" />
          <p className="overline-plum mt-8 mb-4">Your path forward</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">Certification is only <em className="italic font-serif gold-text">the beginning.</em></h2>
          <p className="mt-7 text-aviana-muted leading-[1.9] text-[17px]">
            Once you've found your wings, the lineage keeps unfolding. Experienced Aviana instructors are invited to carry the method into their own communities as authorized Aviana Teacher Trainers — leading trainings, certifying new instructors, and helping the work bloom far beyond one studio. It's a living path of growth, leadership, and creative partnership, walked with Holly every step of the way.
          </p>
          <Link to="/become-an-aerial-yoga-teacher-trainer" className="btn-gold !px-8 !py-4 !text-[13px] mt-10 inline-flex" data-testid="path-forward-cta">
            Become an Aerial Yoga Trainer <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
